import { apiClient, apiClientHooks } from "../../bootstrapping/InitApiClient"
import { Alert } from "@mui/material"
import { refetchProfileJwt } from "../../bootstrapping/InitRequireProfile"
import { useIdTokenClaims } from "../auth0/useIdTokenClaims"
import SingleClickWaitingButton from "../SingleClickWaitingButton"
import LoadingSpinner from "../LoadingSpinner"
import { schemas } from "../../generated/api/client"
import { z } from "zod"
import { queryClient } from "../../bootstrapping/InitReactQuery"

type MyInviteApplicant = z.infer<typeof schemas.MyInviteApplicant>

const oneHourInMillis = 60 * 60 * 1000

const ShowInvites: React.FC<{ noInvites: React.ReactNode }> = ({ noInvites }) => {
  const { idToken } = useIdTokenClaims()

  // Use the idToken to get my invites
  const getMyInvites = apiClientHooks.useGetMyInvites({
    headers: { 'x-auth0-id-token': idToken ?? '' },
  }, {
    enabled: !!idToken,
    staleTime: oneHourInMillis,
  })

  if (getMyInvites.isInitialLoading) return <LoadingSpinner name='ShowInvites' />

  // If there's no data after initial loading (such as an error), default to no invites
  const invites = getMyInvites.data ?? []

  // We only care about Applicant Invites
  const staffInvites = invites.filter((invite): invite is MyInviteApplicant => invite.type === 'applicant')

  // Handler for Accepting the invite
  const onClickAccept = (invite: { id: string }) => async () => {
    if (!idToken) throw new Error('Should not happen: no ID token!')

    const result = await apiClient.acceptInvite(undefined, {
      params: { id: invite.id },
      headers: { 'x-auth0-id-token': idToken },
    })

    if (result.accepted) {
      await refetchProfileJwt()
      queryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getMyInvites') })
      queryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getMyEBulkApplications') })
    }
  }

  return <>
    {staffInvites.length === 0 && noInvites}
    {staffInvites.map(invite => (
      <Alert key={invite.id} severity="success" sx={{ marginBottom: 3 }}>
        You have been invited to apply for a DBS check
        by <strong>{invite.customer_name}</strong> as a <strong>{invite.job_role}</strong>
        {invite.restart_of_application_id && <em> (restarted application)</em>}.
        .
        <br /><br />
        {invite.test_application && <>
          <strong>Please Note:</strong> This is a test application and will not result in a real DBS check.
          <br /><br />
        </>}
        <SingleClickWaitingButton variant="contained" color="primary" disabled={!idToken} onClick={onClickAccept(invite)}>Accept</SingleClickWaitingButton>
      </Alert>
    ))}
  </>
}

export default ShowInvites
